import { useCallback, useState } from 'react';

import { PopoverTrigger } from '@radix-ui/react-popover';
import {
  ColumnFiltersState,
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useParams } from 'react-router-dom';

import { EntityFilters } from './components/entity-filters';
import { EntityLoader } from './components/entity-loader';
import { EntityRow } from './components/entity-row';
import { ButtonGroup } from '../../../../../../components/shadcn/button-group';
import { SubsetChip } from '../../../../../../components/subset-chip';
import { useMatterProcessingStore } from '../../../../../../stores/matter-processing-store';
import { Permission } from '@/../../backend/src/auth/permissions/permission';
import { Person } from '@/../../backend/src/matter/person/person.entity';
import { DocumentDownloadOptions, DownloadFormat, StyleFormat } from '@/../../backend/src/types/types';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useSubsetPersonsDownload } from '@/api/mutations/useDownloadSubsetPersons';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetSubset } from '@/api/queries/useGetSubset';
import { useGetSubsetPersons } from '@/api/queries/useGetSubsetPersons';
import { PermissionGate } from '@/hooks/useHasPermission';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { DownloadDialog } from '@/new/components/dialogs/download-dialog';
import { FilterPills } from '@/new/components/filter-pills';
import { Icon } from '@/new/components/icon';
import { PageContainer } from '@/new/components/page-container';
import { PageHeader } from '@/new/components/page-header';
import { PageTitle } from '@/new/components/page-title';
import { SearchInput } from '@/new/components/search-input';
import { Button } from '@/new/components/shadcn/button';
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/new/components/shadcn/empty';
import { Popover } from '@/new/components/shadcn/popover';
import { TablePagination } from '@/new/components/table/table-pagination';
import { useSubsetBreadcrumbs } from '@/new/hooks/use-subset-breadcrumbs';
import { includedInArr } from '@/new/lib/table-utils';
import { buildClientSideFilterPills } from '@/utils/buildFilterPills';

const fallbackTableData: Person[] = [];
const columnHelper = createColumnHelper<Person>();

const columns = [
  columnHelper.accessor('name', {}),
  columnHelper.accessor('description', {}),
  columnHelper.accessor('type', { filterFn: includedInArr }),
];

export function Entities() {
  const { matterId, subsetId } = useParams();
  if (!matterId || !subsetId) return null;

  const { data: matter } = useGetMatterV2(matterId);
  const { data: subset } = useGetSubset(matterId, subsetId);

  const breadcrumbItems = useSubsetBreadcrumbs({
    matterId,
    subsetId,
    matter,
    subset,
    currentPage: { label: 'Entities', path: 'entities' },
  });

  const { data: allPersons, isLoading } = useGetSubsetPersons(matterId, subsetId);

  const [downloadModalIsOpen, setDownloadModalIsOpen] = useState(false);
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const clearAllFilters = () => {
    setGlobalFilter('');
    setColumnFilters([]);
  };

  const table = useReactTable({
    data: allPersons ?? fallbackTableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => row.id,
    state: {
      globalFilter,
      columnFilters,
    },
    initialState: {
      pagination: {
        pageSize: 50,
      },
    },
    onColumnFiltersChange: setColumnFilters,
  });

  const { mutateAsync: downloadPersons, isLoading: isLoadingDownload } = useSubsetPersonsDownload({
    matterId,
    subsetId,
  });

  const handleDownloadCSV = useCallback(
    async ({
      title,
      columns,
      downloadFormat,
    }: {
      title: string;
      columns: string[];
      downloadFormat: DownloadFormat;
    }) => {
      if (downloadFormat === DownloadFormat.EXCEL) {
        trackCustomEvent(TrackingEventsV2.DocumentDownloadPersonsCsv, {
          matterId,
          isAllDocuments: subset?.isAllDocuments,
        });
      } else if (downloadFormat === DownloadFormat.DOCX) {
        trackCustomEvent(TrackingEventsV2.DocumentDownloadPersonsDocx, {
          matterId,
          isAllDocuments: subset?.isAllDocuments,
        });
      }

      await downloadPersons({ title, columns, styleFormat: StyleFormat.Table, downloadFormat });
      setDownloadModalIsOpen(false);
    },
    [downloadPersons, matterId, subset],
  );

  const handleDownloadWord = useCallback(
    async ({
      title,
      downloadFormat,
      documentOptions,
    }: {
      title: string;
      downloadFormat: DownloadFormat;
      documentOptions: DocumentDownloadOptions;
    }) => {
      trackCustomEvent(TrackingEventsV2.DocumentDownloadPersonsWritten, {
        matterId,
        isAllDocuments: subset?.isAllDocuments,
      });

      await downloadPersons({ title, styleFormat: StyleFormat.Written, downloadFormat, documentOptions });
      setDownloadModalIsOpen(false);
    },
    [downloadPersons, matterId],
  );

  const { getProcessingState } = useMatterProcessingStore();

  return (
    <div className="flex h-full min-w-full flex-col">
      <Breadcrumb items={breadcrumbItems} />

      <div className="flex h-full flex-col overflow-hidden">
        <PageContainer>
          <PageHeader>
            <div className="flex items-center gap-4">
              <PageTitle text="Entities" />
              <SubsetChip subsetId={subsetId} className="mt-0.5" />
            </div>

            <TablePagination table={table} plural="entities" variant="sm" />
          </PageHeader>

          <div className="flex w-full gap-2">
            <SearchInput
              placeholder="Search entities..."
              className="grow"
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
            />

            <PermissionGate
              permission={Permission.PersonDownload}
              uiMode="disabled"
              disabledMessage="You do not have permission to download entities"
            >
              <Button
                onClick={() => setDownloadModalIsOpen(true)}
                disabled={!getProcessingState(matterId)?.hasCompletedRuns}
              >
                <Icon className="lucide--download" />
                <span>Download</span>
              </Button>
            </PermissionGate>

            <ButtonGroup>
              <Popover>
                <PopoverTrigger asChild>
                  <Button disabled={!allPersons}>
                    <Icon className="lucide--funnel-plus" />
                    <span>Add Filters</span>
                  </Button>
                </PopoverTrigger>
                {allPersons && <EntityFilters typeColumn={table.getColumn('type')} clearAllFilters={clearAllFilters} />}
              </Popover>

              <Button onClick={clearAllFilters} disabled={columnFilters.length === 0}>
                <Icon className="lucide--funnel-x" />
                <span>Clear All Filters</span>
              </Button>
            </ButtonGroup>
          </div>

          <div className="my-2.5">
            <FilterPills pills={buildClientSideFilterPills(columnFilters, setColumnFilters)} />
          </div>
        </PageContainer>

        {isLoading ? (
          <div className="mx-5 flex flex-col gap-5 overflow-auto">
            {Array.from({ length: 5 }).map((_, index) => (
              <EntityLoader key={index} />
            ))}
          </div>
        ) : table.getRowModel().rows.length === 0 ? (
          <div className="flex items-center justify-center p-6">
            <Empty>
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <Icon className="lucide--user-round h-5 w-5" />
                </EmptyMedia>
                <EmptyTitle>No entities found</EmptyTitle>
                <EmptyDescription>Sorry, we couldn't find any entities matching these criteria.</EmptyDescription>
              </EmptyHeader>
            </Empty>
          </div>
        ) : (
          <div className="mt-1 flex flex-col gap-4 overflow-auto px-8 pb-8">
            {table.getRowModel().rows.map((row) => (
              <EntityRow key={row.id} person={row.original} />
            ))}
          </div>
        )}
      </div>

      <DownloadDialog
        modalTitle="Export Persons"
        isOpen={downloadModalIsOpen}
        onClose={() => setDownloadModalIsOpen(false)}
        downloadTitle={'Persons'}
        isLoading={isLoadingDownload}
        columnOptions={personsDownloadColumnOptions}
        onDownloadTable={handleDownloadCSV}
        onDownloadWritten={handleDownloadWord}
        displayOptions={{ showStyle: false, showDocumentOptions: true }}
      />
    </div>
  );
}

const personsDownloadColumnOptions = ['Name', 'Type', 'Aliases', 'Description'];
